import { HttpClient } from "@angular/common/http";
import { Component, EventEmitter, Output } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
    faDownload,
    faPlusCircle,
    faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { AsyncModalService } from "src/app/services/shared/async-modal-service";
import { Countries } from "../../../constants/countries";
import { ContactsService } from "../../../services/settings/contacts.service";
import { LookupValuesService } from "../../../services/settings/lookupvalues.service";
import { OrganizationsService } from "../../../services/settings/organizations.service";

@Component({
    standalone: false,
    selector: "contacts-form",
    templateUrl: "./contacts.form.component.html",
    providers: [ContactsService, OrganizationsService, LookupValuesService],
})
export class FormContacts {
    private contactId: number = 0;
    @Output("cancelEdit")
    cancelEdit: EventEmitter<number> = new EventEmitter<number>();
    @Output("refreshTable")
    refreshTable: EventEmitter<void> = new EventEmitter<void>();
    showButtons: boolean = true;
    showImport: boolean;
    isAdd: boolean = false;
    isEdit: boolean = false;
    private form: FormGroup;
    private contact: any = {};
    private fields: Array<FormlyFieldConfig> = [];
    private roles: any[] = [];
    private organizations: any[] = [];
    private locations: any[] = [];
    private disciplines: any[] = [];
    private types: any[] = [];
    private uploadableFile: File;
    private downloadWithData: boolean = false;
    faDownload = faDownload;
    faUpload = faUpload;
    faPlusSign = faPlusCircle;

    constructor(
        private http: HttpClient,
        private fb: FormBuilder,
        private service: ContactsService,
        private organizationService: OrganizationsService,
        private lookupValuesService: LookupValuesService,
        private route: ActivatedRoute,
        private router: Router,
        private asyncModalService: AsyncModalService
    ) {
        this.fields = this.getFieldGroups();
        this.form = fb.group({});
        this.contact = {};
        this.isAdd = false;
        this.showButtons = false;
        this.isEdit = false;
        this.refreshRoles();
        this.refreshOrganizations();
    }

    ngOnInit() {
        this.route.params.subscribe((params) => {
            if (params["id"]) {
                this.contactId = +params["id"];

                this.bindContact(this.contactId);
            } else {
                this.showButtons = true;
            }
        });

        this.lookupValuesService
            .getLookupValues("ContactLocation")
            .subscribe((data) => {
                this.locations = [];

                for (let i = 0; i < data.length; i++) {
                    this.locations.push({
                        value: data[i].name,
                        label: data[i].name,
                    });
                }

                let fieldGroup = this.getFieldGroup("province");

                if (fieldGroup) {
                    fieldGroup.props.options = this.locations;
                }
            });

        this.service.getAllRoles().subscribe((data) => (this.roles = data));

        this.service
            .getDisciplines()
            .subscribe((data) => (this.disciplines = data));

        this.service.getTypes().subscribe((data) => (this.types = data));
    }

    getFieldGroups(): Array<FormlyFieldConfig> {
        return [
            {
                fieldGroup: [
                    {
                        key: "organizationId",
                        type: "hz-select",
                        props: {
                            label: "Organisation",
                            options: this.organizations,
                            required: true,
                        },
                    },
                    {
                        key: "country",
                        type: "hz-select",
                        props: {
                            label: "Country",
                            options: Countries,
                        },
                    },
                    {
                        key: "province",
                        type: "hz-input",
                        props: {
                            type: "text",
                            label: "County/Province",
                            options: this.locations,
                        },
                    },
                    {
                        key: "city",
                        type: "hz-input",
                        props: {
                            type: "text",
                            label: "City/Town",
                        },
                    },
                    {
                        key: "firstName",
                        type: "hz-input",
                        props: {
                            type: "text",
                            label: "First Name",
                            placeholder: "First Name",
                            required: true,
                        },
                    },
                    {
                        key: "lastName",
                        type: "hz-input",
                        props: {
                            type: "text",
                            label: "Last Name",
                            placeholder: "Last Name",
                            required: true,
                        },
                    },
                    {
                        key: "role",
                        type: "hz-select",
                        props: {
                            label: "Role",
                            options: this.roles,
                            required: true,
                        },
                    },
                    {
                        key: "email",
                        type: "hz-input",
                        props: {
                            type: "email",
                            label: "Email",
                            placeholder: "Email",
                        },
                    },
                    {
                        key: "mobileNumber",
                        type: "hz-input",
                        props: {
                            type: "tel",
                            label: "Mobile",
                            placeholder: "0000000000",
                        },
                    },
                    {
                        key: "telephoneNumber",
                        type: "hz-input",
                        props: {
                            type: "tel",
                            label: "Landline",
                            placeholder: "0000000000",
                        },
                    },
                ],
            },
        ];
    }

    ngOnDestroy() {}

    process(contact: FormContacts) {
        if (this.form.valid) {
            if (this.isAdd) {
                this.submitAdd(contact);
            } else {
                this.submitUpdate(contact);
            }
        } else {
            this.asyncModalService.getModalResponse(
                "Please fill in mandatory fields"
            );
        }
    }

    submitUpdate(organization: FormContacts) {
        this.service.updateContact(this.contactId, organization).subscribe(
            null,
            (err) => this.asyncModalService.getModalResponse(err.error),
            () => {
                this.router.navigate(["../../"], { relativeTo: this.route });
            }
        );
    }

    submitAdd(organization: FormContacts) {
        this.service.addContact(organization).subscribe(
            null,
            (err) => this.asyncModalService.getModalResponse(err.error),
            () => {
                this.resetView();

                this.refreshTable.emit();
            }
        );
    }

    resetView() {
        this.contact = {};
        this.contactId = 0;
        this.form.reset();

        this.isAdd = false;
        this.isEdit = false;
        this.showButtons = true;
        this.showImport = false;

        this.cancelEdit.emit(this.contactId);
    }

    showImportView() {
        this.showImport = true;
        this.isAdd = false;
        this.isEdit = false;
        this.showButtons = false;
    }

    fileChangeEvent(fileInput: any) {
        this.uploadableFile = fileInput.target.files[0] as File;
    }

    uploadFile(data) {
        const formData: any = new FormData();
        const file: File = this.uploadableFile;

        formData.append("uploads[]", file, file["name"]);

        this.service.uploadFile(formData).subscribe(
            null,
            (err) => {
                if (err.status == 400)
                    this.asyncModalService.getModalResponse(err.error);

                this.resetView();
                this.refreshTable.emit();
            },
            () => {
                this.resetView();
                this.refreshTable.emit();
            }
        );
    }

    bindContact(organizationId: number) {
        this.service.getContact(organizationId).subscribe(
            (data) => {
                this.contact = data;
                this.form = this.fb.group({});
                this.fields = this.getFieldGroups();
            },
            (err) => this.asyncModalService.getModalResponse(err.error),
            () => {
                this.isEdit = true;
            }
        );
    }

    showAdd() {
        this.contactId = 0;

        this.isAdd = true;
        this.isEdit = false;
        this.showButtons = false;
    }

    cancel() {
        if (this.isEdit) {
            this.router.navigate(["../../"], { relativeTo: this.route });
        } else {
            this.resetView();
        }
    }

    refreshRoles() {
        this.service.getAllRoles().subscribe((data) => {
            this.roles = [];

            for (let i = 0; i < data.length; i++) {
                this.roles.push(data[i]);
            }

            var fieldGroup = this.getFieldGroup("role");

            if (fieldGroup) fieldGroup.props.options = this.roles;
        });
    }

    refreshOrganizations() {
        this.organizationService.getAll().subscribe((data) => {
            this.organizations = data;

            var fieldGroup = this.getFieldGroup("organizationId");

            if (fieldGroup) {
                this.organizations = [];

                for (var i = 0; i < data.length; i++) {
                    this.organizations.push({
                        value: data[i].id,
                        label: data[i].name,
                    });
                }

                fieldGroup.props.options = this.organizations;
            }
        });
    }

    getFieldGroup(key: string): FormlyFieldConfig {
        for (var i = 0; i < this.fields[0].fieldGroup.length; i++) {
            var fieldGroup = this.fields[0].fieldGroup[i];

            if (fieldGroup.key == key) {
                return fieldGroup;
            }
        }
        return null;
    }

    saveAndNew() {
        if (this.form.valid) {
            this.service.addContact(this.contact).subscribe(
                null,
                (err) => {
                    this.asyncModalService.getModalResponse(err.error);
                    this.form.reset();
                    this.refreshTable.emit();
                },
                () => {
                    this.form.reset();
                    this.refreshTable.emit();
                }
            );
        } else {
            this.asyncModalService.getModalResponse(
                "Please fill in mandatory fields"
            );
        }
    }
}
